import * as React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { LocalizedLink } from "@/components/LocalizedLink";
import Navigation from "@/components/Navigation";
import { SeoHead } from "@/components/SeoHead";
import { getSeoCopy } from "@/i18n/seoCopy";
import { useI18n } from "@/i18n";
import { BreadcrumbNav } from "@/components/ui/breadcrumb-nav";
import { Footer } from "@/components/Footer";
import { Award, Shield, Globe, TrendingUp, Heart, Users, Lightbulb, Crown, Target, CheckCircle, Mail, Phone } from "lucide-react";
import aureumLogo from "@/assets/aureum-logo-new.png";
import { GoogleMap } from "@/components/GoogleMap";
import { trackPhoneClick } from "@/components/ConversionTracking";

// Import images
import headquartersImage from "@/assets/headquarters-marbella.jpg";
import officeImage from "@/assets/office-building.webp";

const About = () => {
  const { t, language } = useI18n();
  const seo = getSeoCopy("about", language);
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SeoHead
        route="about"
        schema={{
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "mainEntity": {
            "@type": "Organization",
            "name": "Aureum VII Trading",
            "alternateName": "AVT",
            "foundingDate": "2010",
            "description": "European luxury smart toilet systems and wellness technology innovation",
            "url": "https://www.aureum7trading.com"
          }
        }}
      />
      <Navigation />
      
      {/* Hero Section */}
      <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-12 bg-white">
        <div className="container mx-auto max-w-6xl">
          <BreadcrumbNav items={[
            { label: "Home", href: "/" },
            { label: "About Us" }
          ]} />
          
          <div className="text-center mb-12 sm:mb-16">
            <div className="mb-6 sm:mb-8">
              <img loading="lazy" decoding="async" 
                src={aureumLogo} 
                alt="Aureum VII Trading Logo" 
                className="h-12 sm:h-16 w-auto mx-auto mb-4 sm:mb-6"
              />
            </div>
            <h1 className="font-playfair text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 sm:mb-8 text-primary px-2">
              {seo.h1}
            </h1>
            <p className="font-inter text-lg sm:text-xl md:text-2xl text-muted-foreground leading-relaxed max-w-4xl mx-auto px-4">
              Founded in 2010, we partner with CE-certified manufacturers in China to deliver high-end smart toilets 
              tailored for European markets. Our transparent approach combines Asian manufacturing excellence with 
              European quality standards, offering custom white-label solutions for businesses across the EU.
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-12">
        <div className="container mx-auto max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <h2 className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 sm:mb-8 text-primary">
                Our Mission
              </h2>
              <p className="font-inter text-lg text-muted-foreground leading-relaxed mb-6">
                To bridge the gap between world-class Asian manufacturing and European market demands. We provide 
                transparent, reliable B2B partnerships for businesses seeking premium smart toilet solutions with 
                custom branding, competitive pricing, and full EU compliance.
              </p>
              <p className="font-inter text-lg text-muted-foreground leading-relaxed mb-8">
                Our commitment is to transparency, quality assurance, and long-term partnerships. We handle the 
                complexities of international sourcing, certification, and logistics so you can focus on serving 
                your customers with confidence.
              </p>
              
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 bg-accent/10 rounded-full flex items-center justify-center">
                    <Target className="w-8 h-8 text-accent" />
                  </div>
                  <h3 className="font-playfair text-lg font-semibold mb-2">Excellence</h3>
                  <p className="font-inter text-sm text-muted-foreground">Uncompromising quality in every detail</p>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 bg-accent/10 rounded-full flex items-center justify-center">
                    <Lightbulb className="w-8 h-8 text-accent" />
                  </div>
                  <h3 className="font-playfair text-lg font-semibold mb-2">Innovation</h3>
                  <p className="font-inter text-sm text-muted-foreground">Pioneering wellness technology</p>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img loading="lazy" decoding="async" 
                src={headquartersImage} 
                alt="Aureum VII Trading European headquarters - Modern luxury building in Marbella showcasing contemporary architectural design with golden accent elements reflecting our commitment to premium bathroom technology innovation"
                className="w-full h-96 sm:h-[500px] object-cover rounded-lg shadow-elegant"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-lg"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Brand Story */}
      <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-12 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 text-primary">
              Our Story
            </h2>
            <p className="font-inter text-lg text-muted-foreground max-w-3xl mx-auto">
              From a vision of transforming luxury living to becoming Europe's premier smart toilet specialist
            </p>
          </div>

          <div className="space-y-12 sm:space-y-16">
            {/* Timeline Item 1 */}
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div className="order-2 lg:order-1">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center">
                    <Crown className="w-6 h-6 text-accent" />
                  </div>
                  <span className="font-playfair text-2xl font-bold text-accent">2010</span>
                </div>
                <h3 className="font-playfair text-2xl font-bold mb-4 text-primary">The Foundation</h3>
                <p className="font-inter text-muted-foreground leading-relaxed">
                  Founded with a vision to connect European businesses with high-quality Asian manufacturers. 
                  We established direct partnerships with CE-certified factories in China, ensuring transparent 
                  sourcing and quality control for the European market.
                </p>
              </div>
              <div className="order-1 lg:order-2">
                <div className="h-64 bg-white border border-border/20 rounded-lg shadow-sm flex items-center justify-center">
                  <Award className="w-16 h-16 text-muted-foreground/20" />
                </div>
              </div>
            </div>

            {/* Timeline Item 2 */}
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div>
                <div className="h-64 bg-white border border-border/20 rounded-lg shadow-sm flex items-center justify-center">
                  <Lightbulb className="w-16 h-16 text-muted-foreground/20" />
                </div>
              </div>
              <div>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-accent" />
                  </div>
                  <span className="font-playfair text-2xl font-bold text-accent">2015</span>
                </div>
                <h3 className="font-playfair text-2xl font-bold mb-4 text-primary">B2B Expansion</h3>
                <p className="font-inter text-muted-foreground leading-relaxed">
                  Expanded our white-label program, offering custom branding and OEM solutions for businesses. 
                  Established quality assurance protocols and streamlined logistics for efficient door-to-door 
                  delivery across Europe.
                </p>
              </div>
            </div>

            {/* Timeline Item 3 */}
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div className="order-2 lg:order-1">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center">
                    <Globe className="w-6 h-6 text-accent" />
                  </div>
                  <span className="font-playfair text-2xl font-bold text-accent">2020</span>
                </div>
                <h3 className="font-playfair text-2xl font-bold mb-4 text-primary">Market Leadership</h3>
                <p className="font-inter text-muted-foreground leading-relaxed">
                  Serving B2B partners across Europe including hospitality groups, property developers, and 
                  distributors. Our transparent approach and competitive pricing helped establish us as a 
                  trusted partner for businesses seeking reliable smart toilet solutions.
                </p>
              </div>
              <div className="order-1 lg:order-2">
                <img loading="lazy" decoding="async" 
                  src={officeImage} 
                  alt="AVT modern office complex in Marbella - Contemporary glass and steel construction representing our innovative approach to European luxury smart toilet technology and sustainable design excellence"
                  className="w-full h-64 object-cover rounded-lg shadow-elegant"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-12">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 text-primary">
              Our Values
            </h2>
            <p className="font-inter text-lg text-muted-foreground max-w-3xl mx-auto">
              The principles that guide every innovation, every product, and every client relationship
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="shadow-elegant hover:shadow-gold transition-all duration-500 hover:-translate-y-2">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-6 bg-accent/10 rounded-full flex items-center justify-center">
                  <Heart className="w-8 h-8 text-accent" />
                </div>
                <h3 className="font-playfair text-xl font-bold mb-4 text-primary">Transparency</h3>
                <p className="font-inter text-muted-foreground leading-relaxed">
                  We're honest about our manufacturing partnerships and sourcing. No hidden costs, no misleading 
                  claims—just straightforward B2B relationships built on trust.
                </p>
              </CardContent>
            </Card>

            <Card className="shadow-elegant hover:shadow-gold transition-all duration-500 hover:-translate-y-2">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-6 bg-accent/10 rounded-full flex items-center justify-center">
                  <Shield className="w-8 h-8 text-accent" />
                </div>
                <h3 className="font-playfair text-xl font-bold mb-4 text-primary">CE Certification</h3>
                <p className="font-inter text-muted-foreground leading-relaxed">
                  All products meet CE and RoHS standards for EU compliance. We work exclusively with certified 
                  manufacturers and conduct rigorous quality checks before shipment.
                </p>
              </CardContent>
            </Card>

            <Card className="shadow-elegant hover:shadow-gold transition-all duration-500 hover:-translate-y-2">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-6 bg-accent/10 rounded-full flex items-center justify-center">
                  <Users className="w-8 h-8 text-accent" />
                </div>
                <h3 className="font-playfair text-xl font-bold mb-4 text-primary">B2B Support</h3>
                <p className="font-inter text-muted-foreground leading-relaxed">
                  Dedicated account management, flexible payment terms (30% deposit, 70% before shipment), 
                  and comprehensive support from order to delivery. MOQ: 50 units.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose AVT */}
      <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-12 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 text-primary">
              Why AVT?
            </h2>
            <p className="font-inter text-lg text-muted-foreground max-w-3xl mx-auto">
              What sets us apart in the luxury smart toilet industry
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <CheckCircle className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <h3 className="font-playfair text-lg font-semibold mb-2 text-primary">Transparent Sourcing</h3>
                  <p className="font-inter text-muted-foreground">
                    Direct partnerships with CE-certified Chinese manufacturers. We're transparent about our sourcing 
                    and proud of the quality-to-cost ratio we provide.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <CheckCircle className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <h3 className="font-playfair text-lg font-semibold mb-2 text-primary">Custom White-Labeling</h3>
                  <p className="font-inter text-muted-foreground">
                    OEM branding available. Add your logo, customize packaging, and create your own brand with our 
                    manufacturing partners. Minimum order quantity: 50 units.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <CheckCircle className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <h3 className="font-playfair text-lg font-semibold mb-2 text-primary">Competitive Pricing</h3>
                  <p className="font-inter text-muted-foreground">
                    Volume discounts for bulk orders. Flexible payment terms including escrow and L/C for new partners. 
                    Transparent pricing with no hidden fees.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <CheckCircle className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <h3 className="font-playfair text-lg font-semibold mb-2 text-primary">EU-Wide Shipping</h3>
                  <p className="font-inter text-muted-foreground">
                    Door-to-door delivery via DHL/FedEx. Typical lead time: 4-6 weeks from order confirmation. 
                    All products cleared through EU customs.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <CheckCircle className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <h3 className="font-playfair text-lg font-semibold mb-2 text-primary">Business Support</h3>
                  <p className="font-inter text-muted-foreground">
                    Dedicated B2B account managers, technical specifications, installation guides, and after-sales 
                    support for all business partners.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <CheckCircle className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <h3 className="font-playfair text-lg font-semibold mb-2 text-primary">Sustainability Focus</h3>
                  <p className="font-inter text-muted-foreground">
                    Eco-conscious luxury with water-saving technology and sustainable materials in our wellness systems.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-12 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="font-playfair text-3xl sm:text-4xl font-bold mb-4 text-primary">
              Visit Our European Headquarters
            </h2>
            <p className="font-inter text-lg text-muted-foreground">
              Located in Marbella, Spain - serving luxury markets worldwide
            </p>
          </div>
          <GoogleMap height="450px" className="shadow-elegant" />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-12">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="font-playfair text-3xl sm:text-4xl md:text-5xl font-bold mb-6 text-primary">
            Experience AVT Excellence
          </h2>
          <p className="font-inter text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Discover how our luxury smart toilet systems can transform your space into a wellness sanctuary.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <LocalizedLink to="/smart-toilets">
              <Button className="bg-accent hover:bg-accent/90 text-accent-foreground font-inter font-semibold px-8 py-3 rounded-md shadow-gold">
                Explore Collections
              </Button>
            </LocalizedLink>
            <LocalizedLink to="/contact">
              <Button variant="outline" className="font-inter font-semibold px-8 py-3 rounded-md">
                Partner with Us
              </Button>
            </LocalizedLink>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      {/* Business Identity Clarification */}
      <section className="py-12 px-4 sm:px-6 lg:px-12 bg-muted/30 border-y border-border/10">
        <div className="container mx-auto max-w-4xl">
          <div className="p-6 bg-background rounded-lg shadow-sm border border-border/20">
            <h3 className="font-playfair text-xl font-bold mb-4 text-primary text-center">Important Business Notice</h3>
            <p className="font-inter text-sm text-muted-foreground leading-relaxed text-center">
              <strong>Aureum VII Trading (AVT)</strong> is a specialized B2B supplier and white-label partner for luxury smart bathroom technology. 
              Our business is exclusively focused on premium smart toilet systems, bidet technology, and intelligent bathroom solutions for the European market. 
              We are <strong>not affiliated</strong> with any financial trading companies, forex brokerages, cryptocurrency platforms, or investment firms. 
              Any entities using similar names for financial services are entirely separate and unrelated to our company.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-12 bg-gradient-to-br from-primary/5 via-accent/5 to-primary/5">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 text-primary">
            Ready to Transform Your Bathroom?
          </h2>
          <p className="font-inter text-lg text-muted-foreground mb-8 max-w-2xl mx-auto leading-relaxed">
            Partner with Aureum VII Trading to bring European luxury and intelligent wellness technology to your space. Our expert team is ready to guide you through our premium collections.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button size="lg" asChild className="min-w-[200px]">
              <LocalizedLink to="/contact">Schedule Consultation</LocalizedLink>
            </Button>
            <Button size="lg" variant="outline" asChild className="min-w-[200px]">
              <LocalizedLink to="/smart-toilets">Browse Collections</LocalizedLink>
            </Button>
          </div>
          <div className="mt-8 pt-8 border-t border-border/20">
            <p className="text-sm text-muted-foreground mb-4">Get in touch directly:</p>
            <div className="flex flex-wrap justify-center gap-6">
              <a 
                href="mailto:info@aureum7trading.com" 
                className="flex items-center gap-2 text-accent hover:text-accent/80 transition-colors font-medium"
              >
                <Mail className="w-5 h-5" />
                <span>info@aureum7trading.com</span>
              </a>
              <a 
                href="tel:+34688495309"
                onClick={trackPhoneClick}
                className="flex items-center gap-2 text-accent hover:text-accent/80 transition-colors font-medium"
              >
                <Phone className="w-5 h-5" />
                <span>+34 688 495 309</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default About;